import React from "react";
import { useLanguage } from "@/context/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PANTHER_IMG = `${BACKEND}/api/static/images/black-panther.png`;

/**
 * GloveAnatomy — feature infographic
 *
 * Layout (desktop lg+):
 *   [ Card 01 ]        [ Card 03 ]
 *              [ IMAGE ]
 *   [ Card 02 ]        [ Card 04 ]
 *
 * SVG connector lines run from each card to a target point on the image.
 * On mobile, cards stack below the image in a 2-col grid.
 */

// pointer targets on the image (percentages, x/y from top-left of the SQUARE image)
const TARGETS = {
  punch_zone: { x: 74, y: 24 },   // upper knuckle area of right glove
  breathapren: { x: 66, y: 50 },  // center-right body
  negative_cut: { x: 30, y: 52 }, // center-left palm
  dual_stripe: { x: 62, y: 78 },  // wrist strap area of right glove
};

const FeatureCard = ({ num, name, text, testid }) => (
  <div
    data-testid={testid}
    className="bg-black/60 backdrop-blur-sm border border-white/10 p-5 hover:border-[#00A651]/50 transition-colors"
  >
    <div className="flex items-center gap-3 mb-2">
      <span className="font-condensed uppercase tracking-widest text-[10px] text-[#00A651]">
        {num}
      </span>
      <span className="h-[1px] flex-1 bg-[#00A651]/40" />
    </div>
    <h4 className="font-condensed uppercase tracking-wide text-lg text-white mb-2">
      {name}
    </h4>
    <p className="text-white/70 text-sm leading-relaxed">{text}</p>
  </div>
);

const GloveAnatomy = () => {
  const { t } = useLanguage();
  const a = t.anatomy;

  const byKey = Object.fromEntries(a.features.map((f) => [f.key, f]));

  return (
    <section
      id="anatomy"
      data-testid="anatomy-section"
      className="relative py-24 md:py-32 mx-auto max-w-[1400px] px-6 md:px-12 border-t border-white/5"
    >
      {/* Header */}
      <div className="max-w-3xl mb-14 md:mb-20">
        <div className="font-condensed uppercase tracking-[0.3em] text-xs text-[#00A651] mb-3">
          {a.kicker}
        </div>
        <h2
          data-testid="anatomy-title"
          className="font-brush text-5xl md:text-7xl leading-[0.9] text-white"
        >
          {a.title}
        </h2>
        <p className="mt-6 text-white/70 text-lg leading-relaxed">{a.subtitle}</p>
        <div className="mt-6 inline-flex items-center gap-3 border border-[#00A651]/40 px-3 py-1.5">
          <span className="w-1.5 h-1.5 bg-[#00A651] animate-pulse" />
          <span className="font-condensed uppercase tracking-[0.25em] text-[11px] text-[#00A651]">
            {a.edition_label}
          </span>
        </div>
      </div>

      {/* Desktop grid: cards left / image center / cards right */}
      <div className="hidden lg:grid grid-cols-12 gap-6 items-center relative">
        {/* Left column — 2 cards */}
        <div className="col-span-3 flex flex-col gap-8">
          <FeatureCard testid={`anatomy-card-punch_zone`} num={byKey.punch_zone.num} name={byKey.punch_zone.name} text={byKey.punch_zone.text} />
          <FeatureCard testid={`anatomy-card-negative_cut`} num={byKey.negative_cut.num} name={byKey.negative_cut.name} text={byKey.negative_cut.text} />
        </div>

        {/* Center — image + SVG overlay */}
        <div className="col-span-6 relative">
          <div className="relative aspect-square max-w-[520px] mx-auto">
            {/* radial glow */}
            <div className="absolute inset-0 rounded-full bg-[#00A651]/5 blur-3xl pointer-events-none" />

            <img
              src={PANTHER_IMG}
              alt="4keeperz Panther Edition – Anatomie: Punchingzone, Negative Cut, Breathapren Body, Dual Stripe Verschluss"
              data-testid="anatomy-image"
              className="relative w-full h-full object-contain"
              loading="lazy"
            />

            {/* SVG dot markers (lines are drawn separately in the parent) */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              {Object.entries(TARGETS).map(([key, t]) => (
                <g key={key}>
                  <circle cx={t.x} cy={t.y} r="1.4" fill="#00A651" />
                  <circle
                    cx={t.x}
                    cy={t.y}
                    r="3"
                    fill="none"
                    stroke="#00A651"
                    strokeWidth="0.4"
                    opacity="0.6"
                  >
                    <animate
                      attributeName="r"
                      values="2;4;2"
                      dur="2.5s"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      values="0.7;0.1;0.7"
                      dur="2.5s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </g>
              ))}
            </svg>
          </div>
        </div>

        {/* Right column — 2 cards */}
        <div className="col-span-3 flex flex-col gap-8">
          <FeatureCard testid={`anatomy-card-breathapren`} num={byKey.breathapren.num} name={byKey.breathapren.name} text={byKey.breathapren.text} />
          <FeatureCard testid={`anatomy-card-dual_stripe`} num={byKey.dual_stripe.num} name={byKey.dual_stripe.name} text={byKey.dual_stripe.text} />
        </div>

        {/* Decorative dashed connector lines (desktop-only, absolute over the grid) */}
        <svg
          aria-hidden="true"
          className="absolute inset-0 w-full h-full pointer-events-none hidden xl:block"
          preserveAspectRatio="none"
        >
          {/* Approximate positions: cards are ~25% wide (col-span-3 / 12) at 0-25 and 75-100 % X */}
          {/* We draw lines from card inner edge to image roughly */}
          <line x1="25%" y1="22%" x2="55%" y2="24%" stroke="#00A651" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="4 3" />
          <line x1="25%" y1="78%" x2="45%" y2="60%" stroke="#00A651" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="4 3" />
          <line x1="75%" y1="22%" x2="60%" y2="52%" stroke="#00A651" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="4 3" />
          <line x1="75%" y1="78%" x2="58%" y2="78%" stroke="#00A651" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="4 3" />
        </svg>
      </div>

      {/* Mobile / tablet layout */}
      <div className="lg:hidden">
        <div className="relative aspect-square max-w-[420px] mx-auto mb-8">
          <img
            src={PANTHER_IMG}
            alt="4keeperz Panther Edition – Anatomie"
            className="w-full h-full object-contain"
            loading="lazy"
          />
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            {Object.entries(TARGETS).map(([key, t]) => (
              <circle key={key} cx={t.x} cy={t.y} r="1.6" fill="#00A651" />
            ))}
          </svg>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {a.features.map((feat) => (
            <FeatureCard
              key={feat.key}
              testid={`anatomy-card-mobile-${feat.key}`}
              num={feat.num}
              name={feat.name}
              text={feat.text}
            />
          ))}
        </div>
      </div>

      {/* DUAL STRIPE DEEP DIVE — open vs closed (backhand + palm) comparison */}
      <div
        data-testid="dual-stripe-deepdive"
        className="mt-20 md:mt-28 border border-white/10 bg-gradient-to-br from-[#0A0A0A] to-black overflow-hidden"
      >
        <div className="grid lg:grid-cols-12">
          {/* Text side */}
          <div className="lg:col-span-3 p-8 md:p-10 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-white/10">
            <div className="font-condensed uppercase tracking-[0.3em] text-xs text-[#00A651] mb-3">
              04 · Deep Dive
            </div>
            <h3 className="font-brush text-4xl md:text-5xl leading-[0.95] text-white">
              {a.dual_stripe_deep.title}
            </h3>
            <p className="mt-4 font-condensed uppercase tracking-wide text-white/70 text-sm">
              {a.dual_stripe_deep.subtitle}
            </p>
            <p className="mt-6 text-white/70 text-base leading-relaxed">
              {a.dual_stripe_deep.body}
            </p>
          </div>

          {/* 2 comparison images */}
          <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2">
            {/* OPEN */}
            <div className="relative bg-black border-b md:border-b-0 md:border-r border-white/10">
              <div className="relative aspect-square">
                <img
                  src={`${BACKEND}/api/static/images/dualstripe-open.png`}
                  alt="Dual Stripe Verschluss offen – Latex bis zum Handgelenk sichtbar"
                  data-testid="dualstripe-open-img"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-black/70 backdrop-blur-sm border border-[#00A651]/40 px-3 py-1.5">
                  <span className="w-1.5 h-1.5 bg-[#00A651] animate-pulse" />
                  <span className="font-condensed uppercase tracking-[0.2em] text-[10px] text-[#00A651]">
                    {a.dual_stripe_deep.open_label}
                  </span>
                </div>
              </div>
            </div>
            {/* CLOSED · PALM (Fangfläche) */}
            <div className="relative bg-black">
              <div className="relative aspect-square">
                <img
                  src={`${BACKEND}/api/static/images/dualstripe-closed-palm.png`}
                  alt="Dual Stripe Verschluss geschlossen – Fangfläche / Palm-Latex ohne Strap durchgehend"
                  data-testid="dualstripe-closed-palm-img"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-black/70 backdrop-blur-sm border border-[#00A651]/40 px-3 py-1.5">
                  <span className="w-1.5 h-1.5 bg-[#00A651]" />
                  <span className="font-condensed uppercase tracking-[0.2em] text-[10px] text-[#00A651]">
                    {a.dual_stripe_deep.closed_palm_label}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GloveAnatomy;
